import { useState } from "react";
import {
  API_BASE,
  useAuthProviders,
  useRequestMagicLink,
  type MagicLinkResponse,
} from "@vorloop/api-client-react";
import { IconGoogle, IconGithub } from "../components/layout/Icons";

/**
 * Magic-link sign-in. Per spec/04-auth.md §2.3.
 * In dev mode (VORLOOP_EXPOSE_DEV_TOKEN=1) the server returns a clickable
 * URL so we can sign in without configuring email.
 */
/** Map Better Auth magic-link error codes (sent via ?error= on redirect)
 * to user-friendly copy. */
const ERROR_MESSAGES: Record<string, string> = {
  EXPIRED_TOKEN: "That sign-in link expired. Magic links are valid for 10 minutes — request a fresh one below.",
  INVALID_TOKEN: "That sign-in link is invalid or has already been used. Request a fresh one below.",
};

function readErrorFromUrl(): string | null {
  try {
    const code = new URLSearchParams(window.location.search).get("error");
    if (!code) return null;
    return ERROR_MESSAGES[code] ?? `Sign-in failed (${code}). Please try again.`;
  } catch {
    return null;
  }
}

export function SignIn() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<MagicLinkResponse | null>(null);
  const [linkError] = useState<string | null>(() => readErrorFromUrl());
  const request = useRequestMagicLink({
    onSuccess: (r) => setResult(r),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let after = "/";
    try {
      after = sessionStorage.getItem("vp.afterSignin") ?? "/";
    } catch {
      // ignore
    }
    request.mutate({ email, redirectTo: `${window.location.origin}${after}` });
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg-canvas)",
        color: "var(--text-primary)",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 28,
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 14,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            aria-hidden
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "linear-gradient(135deg,#F59E0B,#EA580C)",
              display: "inline-block",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Sign in to Vor</span>
            <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              Magic link — no password.
            </span>
          </div>
        </div>

        {linkError && !result && (
          <div
            role="alert"
            style={{
              padding: "10px 12px",
              background: "rgba(248,113,113,0.10)",
              border: "1px solid rgba(248,113,113,0.30)",
              borderRadius: 8,
              color: "var(--accent-red)",
              fontSize: 12.5,
              lineHeight: 1.45,
              marginBottom: 12,
            }}
          >
            {linkError}
          </div>
        )}

        {result ? (
          <SuccessPanel result={result} email={email} onTryAnother={() => setResult(null)} />
        ) : (
          <>
            <OAuthRow />
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label htmlFor="email" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                Email
              </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "var(--bg-canvas)",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
                color: "var(--text-primary)",
                fontSize: 14,
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={request.isPending || !email}
              style={{
                padding: "10px 14px",
                background: request.isPending || !email ? "var(--bg-active)" : "var(--accent-blue)",
                color: request.isPending || !email ? "var(--text-tertiary)" : "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                cursor: request.isPending || !email ? "not-allowed" : "pointer",
                fontSize: 14,
              }}
            >
              {request.isPending ? "Sending…" : "Email me a magic link"}
            </button>
            {request.error && (
              <p style={{ color: "var(--accent-red)", fontSize: 12, margin: 0 }}>
                {request.error.message}
              </p>
            )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Renders OAuth buttons + the "or sign in with email" divider together,
 * so when no providers are configured (early prod) we hide the divider
 * too and just show the email form.
 */
function OAuthRow() {
  const { data } = useAuthProviders();
  if (!data || (!data.google && !data.github)) return null;
  return (
    <>
      <OAuthButtons />
      <DividerWithLabel label="or sign in with email" />
    </>
  );
}

function OAuthButtons() {
  // Better Auth's social sign-in endpoint takes a JSON body via POST and
  // returns {url} pointing at the provider's consent screen — we then
  // navigate the browser there. (There is no GET redirect endpoint;
  // an earlier version of this code assumed there was and 404'd.)
  const { data } = useAuthProviders();

  async function signInWith(provider: "google" | "github") {
    const callbackURL = `${window.location.origin}/`;
    try {
      const res = await fetch(`${API_BASE}/api/auth/sign-in/social`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ provider, callbackURL }),
      });
      const json = (await res.json().catch(() => null)) as { url?: string; redirect?: string } | null;
      const target = json?.url ?? json?.redirect;
      if (!res.ok || !target) {
        throw new Error(`sign-in failed (${res.status})`);
      }
      window.location.href = target;
    } catch (err) {
      // Surface in the URL so the existing error banner picks it up.
      window.location.href = `/signin?error=OAUTH_FAILED&detail=${encodeURIComponent((err as Error).message)}`;
    }
  }

  // While loading, render nothing so we don't briefly flash buttons that
  // get hidden a frame later.
  if (!data) return null;
  if (!data.google && !data.github) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {data.google && (
        <button type="button" onClick={() => signInWith("google")} style={oauthButtonStyle}>
          <IconGoogle size={16} />
          <span>Continue with Google</span>
        </button>
      )}
      {data.github && (
        <button
          type="button"
          onClick={() => signInWith("github")}
          style={{ ...oauthButtonStyle, background: "#0d1117", color: "#fff", borderColor: "#0d1117" }}
        >
          <IconGithub size={16} />
          <span>Continue with GitHub</span>
        </button>
      )}
    </div>
  );
}

function DividerWithLabel({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
      <span style={{ fontSize: 10, color: "var(--text-tertiary)", letterSpacing: 0.4, textTransform: "uppercase" }}>
        {label}
      </span>
      <span style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
    </div>
  );
}

const oauthButtonStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: "10px 14px",
  background: "#fff",
  color: "#1f2937",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

function SuccessPanel({
  result,
  email,
  onTryAnother,
}: {
  result: MagicLinkResponse;
  email: string;
  onTryAnother: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ color: "var(--text-secondary)", lineHeight: 1.5, margin: 0, fontSize: 13 }}>
        Magic link sent to <strong style={{ color: "var(--text-primary)" }}>{email}</strong>. Open it on
        this device to sign in.
      </p>

      {result.devUrl && (
        <div
          style={{
            padding: 12,
            background: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.35)",
            borderRadius: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            fontSize: 12,
            color: "var(--text-secondary)",
          }}
        >
          <span>
            <strong style={{ color: "var(--accent-amber)" }}>Dev mode:</strong> email isn't configured,
            so use this one-time link.
          </span>
          <a
            href={result.devUrl}
            style={{
              padding: "8px 12px",
              background: "var(--accent-blue)",
              color: "#fff",
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              textAlign: "center",
              textDecoration: "none",
            }}
          >
            Sign in now →
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={onTryAnother}
        style={{
          padding: "8px 12px",
          background: "transparent",
          color: "var(--text-tertiary)",
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        Use a different email
      </button>
    </div>
  );
}
